import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup, UntypedFormGroup } from '@angular/forms';
import { DeviceUser } from '@core/models/device-user';
import { User } from '../../models';
import { DeviceUserFormControlName, MyDeviceFormControlName } from './models';
import { RegistrationDeviceFormService } from './services/registration-device-form.service';
import { RegisteredDevicesFormService } from './services/registered-devices-form.service';
import {
  MyDevicesFormBuilderService,
  MyDevicesFormState
} from './types/my-devices-form-builder-service.type';
import { NewDeviceRegistrationFormService } from './services/new-device-registration-form.service';

const IS_DEBUG_MODE = false;
const LOG_PREFIX = '[PROFILE:MY_DEVICES] : ';

@Component({
    selector: 'app-my-device-form',
    templateUrl: './my-device-form.component.html',
    styleUrls: ['./my-device-form.component.scss'],
    standalone: false
})
export class MyDeviceFormComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('user') user$!: Observable<User | undefined>;
  @Input() navigationBackButton = false;
  @Input() addAnotherDeviceButton = false;
  @Input() label!: string;
  @Input() hasProfile!: boolean;

  @Output() devicesSubmit = new EventEmitter<{
    devices: DeviceUser[];
    hasNewDevices: boolean;
  }>();
  @Output() backCurrent = new EventEmitter<void>();

  @Output() removeDevice: EventEmitter<{ userId: number; device: DeviceUser }> = new EventEmitter<{
    userId: number;
    device: DeviceUser;
  }>();

  form!: UntypedFormGroup;
  registeredUserDevicesBySerial: { [key: string]: DeviceUser } = {};

  protected myDevicesFormState: MyDevicesFormState = {
    isSubmitting: false,
    hasError: false
  };
  private formBuilderService!: MyDevicesFormBuilderService;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly newDeviceRegistrationFormService: NewDeviceRegistrationFormService,
    private readonly registrationDeviceFormService: RegistrationDeviceFormService,
    private readonly registeredDevicesFormService: RegisteredDevicesFormService
  ) {}

  protected get registeredDevices(): FormArray {
    return this.form.get(MyDeviceFormControlName.REGISTERED_DEVICES) as FormArray;
  }

  protected get newDevices(): FormArray {
    return this.form.get(MyDeviceFormControlName.NEW_DEVICES) as FormArray;
  }

  protected get allDeviceControls(): AbstractControl<any, any>[] {
    return [...this.registeredDevices.controls, ...this.newDevices.controls];
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  onNavigationBack(): void {
    this.backCurrent.emit();
  }

  onChangedPassphrase(value: string): void {
    this.myDevicesFormState.isSubmitting = false;
  }

  onAddNewDevice(): void {
    this.newDevices.push(
      this.newDeviceRegistrationFormService.buildNewDeviceFormGroup(this.myDevicesFormState)
    );

    this.form.markAsDirty();
  }

  onRemoveAddAnotherDevice(index: number): void {
    this.myDevicesFormState.isSubmitting = false;
    this.newDevices.removeAt(index);
  }

  async onUnlinkSerial(event: { userId: number; device: DeviceUser }) {
    this.removeDevice.emit(event);
  }

  onSubmit(): void {
    // NOTE:
    // We want to validate form by submit
    this.myDevicesFormState.isSubmitting = true;
    this.myDevicesFormState.hasError = false;
    this.markAllEnabledSerialsAsDirty();
    this.validateDevices();
    // NOTE:
    // submit by status -> see 'initSubscriptions' and 'createForm$'
  }

  isDisabledUpdateButton(): boolean {
    if (!this.hasProfile) {
      return !this.newDevices.controls.some((c) => !!c.value?.[DeviceUserFormControlName.SERIAL]);
    }

    return !this.form.dirty;
  }

  private markAllEnabledSerialsAsDirty() {
    this.allDeviceControls.forEach((deviceControl) => {
      deviceControl.markAsDirty();
      Object.values((<FormGroup>deviceControl).controls).forEach((control) => {
        control.markAsDirty();
      });
    });
  }

  private validateDevices() {
    this.allDeviceControls.forEach((deviceControl) => {
      deviceControl.updateValueAndValidity();
      Object.values((<FormGroup>deviceControl).controls).forEach((control) => {
        control.updateValueAndValidity();
      });
    });
  }

  private getSubmittedDevicesData(): { devices: DeviceUser[]; hasNewDevices: boolean } {
    const data = this.form.getRawValue();
    return {
      devices: [
        ...data[MyDeviceFormControlName.REGISTERED_DEVICES],
        ...data[MyDeviceFormControlName.NEW_DEVICES]
      ],
      hasNewDevices: data[MyDeviceFormControlName.NEW_DEVICES].length > 0
    };
  }

  private initSubscriptions() {
    const createForm$ = this.user$.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((user) => {
        (user?.devices ?? []).forEach((device) => {
          this.registeredUserDevicesBySerial[device.serial] = device;
        });
      }),
      map((user) => {
        this.formBuilderService = this.hasProfile
          ? this.registeredDevicesFormService
          : this.registrationDeviceFormService;
        this.form = this.formBuilderService.initForm(user?.devices, this.myDevicesFormState);
        this.formBuilderService.configureForm(this.form);

        if (IS_DEBUG_MODE) {
          console.debug(`${LOG_PREFIX} form :`, this.form);
        }

        return this.form;
      })
    );

    const formStatusChanges$ = createForm$.pipe(switchMap((form) => form.statusChanges));

    this.user$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user) => !!this.form && !!user),
        tap((user) => {
          this.registeredDevices?.reset(
            user!.devices.map(({ serial, passphrase }) => ({
              [DeviceUserFormControlName.SERIAL]: serial,
              [DeviceUserFormControlName.PASSPHRASE]: passphrase
            }))
          );

          this.formBuilderService.configureForm(this.form);
        })
      )
      .subscribe();

    formStatusChanges$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.myDevicesFormState.isSubmitting),
        filter((statusChanges) => ['VALID', 'DISABLED'].includes(statusChanges)),
        tap(() => {
          this.myDevicesFormState.isSubmitting = false;
          this.devicesSubmit.emit(this.getSubmittedDevicesData());
        })
      )
      .subscribe();
  }
}
